import React, { useState, useEffect } from 'react'
import { Link, Switch } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import { Header, PeractoLogo } from '@peracto/peracto-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/pro-regular-svg-icons/faBars'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { LoadingIcon } from '@peracto/peracto-ui'

import Menu from '../Menu'
import Dashboard from '../Dashboard'
import Configuration from '../Configuration'
import PrivateRoute from '../PrivateRoute'
import NoMatchRoute from '../NoMatchRoute'

// We need to use the settings provider here as the user may
// not be authorised if we used it at a higher level in the app
import { SettingsProvider } from '@peracto/peracto-hooks'

import { AdminStyle } from '../../theme'

import * as S from './styled'
import { useClient, GET_LIST } from '@peracto/client'

const Layout = ({ routes }) => {
    const [isOpen, setIsOpen] = useState(true)
    const { client, loading } = useClient()
    const [settings, setSettings] = useState()
    const [layoutLoading, setLayoutLoading] = useState(true)

    useEffect(() => {
        // Close menu on initial load on mobile
        if (!window.matchMedia('(min-width: 768px)').matches) {
            setIsOpen(false)
        }
    }, [])

    useEffect(() => {
        if (!loading && typeof client === 'function') {
            fetchSettings()
        }
    }, [loading, client])

    const fetchSettings = async () => {
        try {
            const { data } = await client(GET_LIST, 'configurations', {
                id: 'configurations',
            })

            const settingsData = {
                groups: {},
                values: {},
            }

            data.forEach(row => {
                settingsData.groups = {
                    ...settingsData.groups,
                    ...row.groups,
                }

                for (let key in row.values) {
                    if (key === 'status') {
                        if (row.name == 'Products') {
                            if (row?.values?.[key]?.length > 0) {
                                settingsData.values.productStatuses = row?.values[key]
                            } else {
                                settingsData.values.productStatuses = ['draft', 'active', 'deleted']
                            }
                        }

                        if (row.name == 'Orders') {
                            if (row?.values?.[key]?.length > 0) {
                                settingsData.values.orderStatuses = row?.values[key]
                            } else {
                                settingsData.values.orderStatuses = [
                                    'abandoned',
                                    'awaiting_action',
                                    'cancelled',
                                    'complete',
                                    'deposit_paid',
                                    'dispatched',
                                    'packing',
                                    'part_complete',
                                    'part_returned',
                                    'pending',
                                    'processing',
                                    'returned',
                                ]
                            }
                        }
                    } else {
                        settingsData.values[key] = row?.values[key]
                    }
                }
            })
            setSettings(settingsData)
        } catch (e) {
            console.error(e)
        }
        setLayoutLoading(false) // ensure settings are loaded before rendering layout
    }

    if (layoutLoading) {
        return (
            <div style={{ height: '100vh' }}>
                <div className="!flex !grow !items-center !flex-col !justify-center !h-full">
                    <LoadingIcon />
                </div>
            </div>
        )
    }

    return (
        <SettingsProvider settings={settings}>
            <Helmet>
                <title>Peracto</title>
            </Helmet>
            <S.LayoutContainer>
                <AdminStyle />
                <Header>
                    <div className="sticky-content">
                        <div className="!flex !justify-between !items-center">
                            <Link to="/">
                                <PeractoLogo height={40} />
                            </Link>

                            <S.MobileToggle
                                className="!flex lg:!hidden"
                                onClick={() => setIsOpen(!isOpen)}
                            >
                                <FontAwesomeIcon icon={isOpen ? faTimes : faBars} size="lg" />
                            </S.MobileToggle>
                        </div>

                        <S.Sidebar>
                            <Menu isOpen={isOpen} setIsOpen={setIsOpen} />
                        </S.Sidebar>
                    </div>
                </Header>

                <S.Content>
                    <div className="!mb-12 container-fluid lg:!px-12">
                        <Switch>
                            <PrivateRoute path="/" exact component={Dashboard} />
                            <PrivateRoute path="/settings" exact component={Configuration} />
                            {[...routes, <NoMatchRoute key="no-match" />]}
                        </Switch>
                    </div>
                </S.Content>
            </S.LayoutContainer>
        </SettingsProvider>
    )
}

export default Layout
